import React, { useEffect, useRef, useState } from 'react';
import { useRecoilCallback, useRecoilRefresher_UNSTABLE, useRecoilValue, useSetRecoilState } from 'recoil';
import { MenuItem, Popover, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useAuth, useConfirm, useLayoutsRouter, useLocales } from '../../hooks';
import { AddOutlined, MoreVertOutlined } from '@mui/icons-material';
import {
  withAllLayouts,
  withLayoutsType,
  withLayoutsTypeLowercase,
  withNewLayout,
  withShowLayoutForm,
  withLayoutSplitPane
} from '../../state/Layouts';
import LayoutView from './LayoutView';
import LayoutForm from './LayoutForm';
import UIModuleForm from './UIModuleForm';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';
import PageForm from './PageForm';
import LayoutWarningsModal from './LayoutWarningsModal';
import { LayoutsPageSelector } from './LayoutsPageSelector';
import { useSplitPane } from '../../hooks/General/useSplitPane';
import { Allotment } from 'allotment';
import { LayoutsExportModal } from './LayoutsExportModal';
import { useData } from '../../data-layer';
import { LayoutsRouter } from './LayoutsRouter';
import { LayoutsCountryFilter } from './LayoutsCountryFilter';
import { LayoutsTypeSelector } from './LayoutTypeSelector';
import { LayoutsViewTabs } from './LayoutsViewTabs';
import { usePermissionsGuard } from '../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../state/theme';
import { layoutsTestIds } from '../shared/TestsIds';
import { UserResponse } from '../../API';
import { pageToPathParam } from '../../utils/layouts';
import { DUMMY_LAYOUT_ID } from '../../utils/appDefaults';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex'
  },
  leftColumn: {
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  layoutTypeSelect: {
    maxWidth: 120
  },
  topPanel: {
    padding: theme.spacing(4, 0, 1),
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  topLine: {
    display: 'flex',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    alignItems: 'flex-start',
    justifyContent: 'space-between'
  },
  title: {
    marginBlockStart: 10,
    marginRight: theme.spacing(4)
  },
  pageSelect: {
    width: '100%',
    display: 'inline-block'
  },
  countryPicker: {
    margin: theme.spacing(0, 2, 0, 4)
  },
  viewTabs: {
    marginTop: theme.spacing(3)
  },
  bottomPanel: {
    padding: theme.spacing(0, 4),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: theme.spacing(8),
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

function Layouts(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const newLayout = useSetRecoilState(withNewLayout);
  const allLayouts = useRecoilValue(withAllLayouts);
  const showingLayoutForm = useRecoilValue(withShowLayoutForm);
  const layoutsType = useRecoilValue(withLayoutsType);
  const layoutsTypeLowercase = useRecoilValue(withLayoutsTypeLowercase);
  const [pageMenuAnchorEl, setPageMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { canSave, isAdmin } = usePermissionsGuard({
    homepageOption: HomepageOptions.LAYOUTS
  });
  const { sizes, onChange: onSplitPaneChange } = useSplitPane(withLayoutSplitPane);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const buttonRef = useRef<HTMLLIElement>(null);

  const {
    pages: {
      state: { withRecordBucket: withPagesByType, withSelected: withSelectedPage },
      hook: { new: setNewPage, remove: removePage }
    },
    users: {
      hook: { update: updateUser }
    }
  } = useData();
  const pages = useRecoilValue(withPagesByType(layoutsType));
  const selectedPage = useRecoilValue(withSelectedPage);

  const { currentUser, updateCurrentUser } = useAuth();
  const shouldHideLayout = useRef(false);
  const { confirm } = useConfirm();
  const { updateLayoutsRoute } = useLayoutsRouter();
  const refetchPages = useRecoilRefresher_UNSTABLE(withPagesByType(layoutsType));
  const refetchLayouts = useRecoilRefresher_UNSTABLE(withAllLayouts);

  useEffect(() => {
    if (currentUser?.id && selectedPage?.urlPath) {
      shouldHideLayout.current = !currentUser.hiddenLayouts?.includes(selectedPage.urlPath);
    }
  }, [currentUser, selectedPage]);

  const openPageMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPageMenuAnchorEl(event.currentTarget);
  };

  const closePageMenu = () => {
    setPageMenuAnchorEl(null);
  };

  const newPage = () => {
    closePageMenu();
    setNewPage();
  };

  const onDownload = () => {
    closePageMenu();
    setCsvModalOpen(true);
  };

  const handleNewLayoutClick = () => {
    if (!selectedPage) return;
    newLayout(selectedPage);
  };

  const noPageSelected = pages && !selectedPage;

  async function hideLayoutUrl() {
    if (currentUser?.id && selectedPage?.urlPath) {
      const result = await updateUser(currentUser?.id, {
        ...currentUser,
        hiddenLayouts: [...(currentUser?.hiddenLayouts || []), selectedPage.urlPath]
      });

      updateCurrentUser(result as UserResponse);
    }
  }

  async function unHideLayoutUrl() {
    if (currentUser?.id && selectedPage?.urlPath) {
      const result = await updateUser(currentUser?.id, {
        ...currentUser,
        hiddenLayouts: currentUser.hiddenLayouts?.filter((layout) => layout !== selectedPage?.urlPath) || []
      });

      updateCurrentUser(result as UserResponse);
    }
  }

  const handleDeletePage = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        closePageMenu();

        if (!selectedPage) return;

        const result = await confirm({
          confirmColor: 'error',
          confirmText: t('general.confirm_delete'),
          body: t('layouts.delete_page_popup_confirm')
        });

        if (result) {
          await removePage(selectedPage.urlPath);
          refetchPages();
          refetchLayouts();

          // Wait for Recoil to update with the refetched data
          await new Promise((resolve) => setTimeout(resolve, 0));

          // Get the updated pages list after refetch
          const updatedPages = snapshot.getLoadable(withPagesByType(layoutsType)).getValue();

          if (updatedPages && updatedPages.length > 0) {
            const firstAvailablePage = updatedPages[0];
            updateLayoutsRoute({
              pageUrl: pageToPathParam(firstAvailablePage),
              layoutId: DUMMY_LAYOUT_ID
            });
          }
        }
      },
    [selectedPage, layoutsType, t]
  );

  async function handleHideLayoutUrl(event: React.MouseEvent<HTMLLIElement>) {
    event.preventDefault();
    event.stopPropagation();
    closePageMenu();

    if (shouldHideLayout.current) {
      await hideLayoutUrl();
    } else {
      await unHideLayoutUrl();
    }
  }

  return (
    <>
      <LayoutsRouter />
      <Allotment
        defaultSizes={sizes}
        onChange={onSplitPaneChange}
        data-testid={layoutsTestIds.componentRoot}
        className={classes.root}
      >
        <Allotment.Pane minSize={420} preferredSize={555} maxSize={800}>
          <div className={classes.leftColumn}>
            <div className={classes.topPanel}>
              <div className={classes.topLine}>
                <Typography className={classes.title} variant="h5">
                  {t('layouts.layouts')}
                </Typography>
                <LayoutsTypeSelector
                  className={classes.layoutTypeSelect}
                  data-testid={layoutsTestIds.layoutsTypeSelector}
                />
                <LayoutsPageSelector className={classes.pageSelect} />
                <IconButton
                  onClick={openPageMenu}
                  disabled={!pages}
                  data-testid={layoutsTestIds.pageMenuButton}
                  size="large"
                  title={t(`layouts.${layoutsTypeLowercase}_actions`)}
                >
                  <MoreVertOutlined />
                </IconButton>
                <Popover
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  anchorEl={pageMenuAnchorEl}
                  keepMounted
                  open={Boolean(pageMenuAnchorEl)}
                  onClose={closePageMenu}
                >
                  <MenuItem onClick={newPage} data-testid={layoutsTestIds.newPageButton} disabled={!canSave}>
                    {t(`layouts.create_new_${layoutsTypeLowercase}`)}
                  </MenuItem>
                  <MenuItem ref={buttonRef} onClick={onDownload}>
                    {t('layouts.download_csv')}
                  </MenuItem>
                  <MenuItem
                    onClick={handleHideLayoutUrl}
                    data-testid={layoutsTestIds.hideLayoutUrl}
                    disabled={!canSave}
                  >
                    {t(`layouts.${shouldHideLayout.current ? 'hide' : 'unhide'}_layout_url`)}
                  </MenuItem>
                  <MenuItem
                    onClick={handleDeletePage}
                    disabled={!canSave || !isAdmin}
                    data-testid={layoutsTestIds.deletePageButton}
                  >
                    {t('layouts.delete_page')}
                  </MenuItem>
                </Popover>
              </div>
              <LayoutsCountryFilter className={classes.countryPicker} />
            </div>
            {!noPageSelected && (
              <>
                <LayoutsViewTabs className={classes.viewTabs} />
                <div className={classes.bottomPanel}>
                  <div></div>
                  <div>
                    <Button
                      endIcon={<AddOutlined />}
                      disabled={!allLayouts || showingLayoutForm || !canSave}
                      onClick={handleNewLayoutClick}
                      data-testid={layoutsTestIds.newLayoutButton}
                    >
                      {t(`layouts.new_${layoutsTypeLowercase}_layout`)}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Allotment.Pane>
        <Allotment.Pane>
          <LayoutView />
        </Allotment.Pane>
      </Allotment>
      <PageForm />
      <LayoutForm />
      <UIModuleForm hasUpsertPermission={canSave} />
      <LayoutWarningsModal />
      <LayoutsExportModal open={csvModalOpen} onClose={() => setCsvModalOpen(false)} />
    </>
  );
}

export default Layouts;
